import {
  call, put, takeLatest, takeEvery, all, select
} from 'redux-saga/effects';
import defaultFetch from 'react/api/sagas/fetch/defaultFetch.js';
import { actions, actionCreators } from 'react/api/actions/routeSurveyModal';
import * as selectors from 'react/api/selectors/routeSurveyModal';

// START: HANDLE TOASTS
function* handleToasts(action) {
  const { routeId, stopId } = action.payload;

  if (action.payload?.errorResource) {
    const message = Monet.I18n.getResourcesByKey(action.payload.errorResource, action.payload.surveyQuestion?.get('question'));

    yield put(actionCreators.enqueueToast(routeId, stopId, { type: 'error', message }));
  }
}

function* watchFailedFetchs() {
  yield takeLatest([
    actions.fetchSurveysFailed,
    actions.fetchPhotoAlbumsFailed,
    actions.fetchEntityAnswerFailed,
    actions.fetchSignatureAnswerFailed
  ], handleToasts);
}

// END: HANDLE TOASTS

// START: FETCH SURVEYS APPLIED AND SURVEY
function* fetchSurveysAppliedApi(routeId, stopId) {
  const criteria = {
    filters: [
      'id',
      'when',
      'route.id',
      'stop.id',
      'stop.location.description',
      'stop.location.key',
      'stop.actualSequenceNum',
      'surveyAnswers.id',
      'surveyAnswers.question.id',
      'surveyAnswers.valueAnswered',
      'surveyApplyRule.*',
      'order.*',
      'order.lineItems.totalPrice',
      'order.size1AliasConfig.key',
      'order.size2AliasConfig.key',
      'order.size3AliasConfig.key'
    ]
  };

  const criteriaObject = [
    { attr: 'route.id', eq: routeId },
  ];

  stopId !== 'without-stop' && criteriaObject.push({ attr: 'stop.id', eq: stopId });

  const data = { criteriaChain: [{ and: criteriaObject }] };
  const url = `/SurveyApplied/restrictions?criteria=${JSON.stringify(criteria)}`;
  const encodedUrl = encodeURI(url);
  return yield call(defaultFetch, encodedUrl, data, 'POST');
}

function* fetchSurveysApi(surveysApplied) {
  const surveyApplyRulesIds = [...surveysApplied.reduce((acc, { surveyApplyRule }) => {
    acc.add(surveyApplyRule.id);
    return acc;
  }, new Set())];
  const criteria = {
    filters: [
      'id',
      'title',
      'surveyApplyRules.id',
      'surveySections.*',
      'surveySections.surveyQuestions.*',
      'surveySections.surveyQuestions.subQuestions.*',
      'surveySections.surveyQuestions.subQuestions.surveyQuestionDomain.*',
      'surveySections.surveyQuestions.surveyQuestionDomain.*',
      'surveySections.surveyQuestions.entity.*'
    ],
  };
  const data = {
    criteriaChain: [{
      and: [
        { attr: 'surveyApplyRules.id', in: surveyApplyRulesIds },
        { attr: 'surveySections.surveyQuestions.parentQuestion.id', isNull: true }
      ]
    }]
  };
  const url = `/Survey/restrictions?criteria=${JSON.stringify(criteria)}`;
  const encodedUrl = encodeURI(url);
  return yield call(defaultFetch, encodedUrl, data, 'POST');
}

function* fetchSurveys(action) {
  const { routeId, stopId } = action.payload;
  const surveysApplied = yield call(fetchSurveysAppliedApi, routeId, stopId);

  if (surveysApplied?.length === 0 && !surveysApplied?.error) {
    yield put(actionCreators.fetchSurveysFailed(
      {
        errorResource: null,
        routeId,
        stopId
      }));

    return;
  }

  if (!surveysApplied.error) {
    const surveys = yield call(fetchSurveysApi, surveysApplied);
    if (!surveys.error) {
      yield put(actionCreators.fetchSurveysSucceeded({ surveysApplied, surveys, routeId, stopId }));
      return;
    }
  }


  yield put(actionCreators.fetchSurveysFailed({ errorResource: 'routeSurveyModal.fetchSurveysError', routeId, stopId }));
}

function* watchFetchSurveys() {
  yield takeLatest(actions.fetchSurveys, fetchSurveys);
}
// END: FETCH SURVEYS APPLIED AND SURVEY

// START: FETCH PHOTO ALBUMS
function* fetchPhotoAlbumsApi(filter) {
  const url = '/PhotoAlbum/FindBySurveyQuestion';
  return yield call(defaultFetch, url, filter, 'POST');
}

function* fetchPhotoAlbums(action) {
  const { routeId, stopId } = action.payload;

  const selectedIndex = yield select((state) => {
    return selectors.getSelectedIndex(state, routeId, stopId);
  });

  const filter = yield select((state) => {
    return selectors.getPhotoAlbumFilter(state, routeId, stopId);
  });

  const photoAlbums = yield call(fetchPhotoAlbumsApi, filter);

  if (!photoAlbums.error) {
    yield put(actionCreators.fetchPhotoAlbumsSucceeded({ photoAlbums, selectedIndex, routeId, stopId }));
  } else {
    yield put(actionCreators.fetchPhotoAlbumsFailed({ errorResource: 'routeSurveyModal.fetchPhotosError', selectedIndex, routeId, stopId }));
  }
}

function* watchFetchPhotoAlbums() {
  yield takeEvery(actions.fetchPhotoAlbums, fetchPhotoAlbums);
}
// END: FETCH PHOTO ALBUMS

// START: FETCH ENTITY ANSWER
function* fetchEntityAnswerApi({ surveyQuestion }) {
  const entityKey = surveyQuestion.getIn(['entity', 'key']);
  const entityProjection = surveyQuestion.get('entityProjection');
  const entityRestriction = surveyQuestion.get('entityRestriction');
  const url = `/${entityKey}/restrictions?criteria=${entityProjection}`;
  const data = JSON.parse(entityRestriction);
  data.criteriaChain[0].and.push({ attr: 'id', in: surveyQuestion.get('answers').toJS() });
  return yield call(defaultFetch, encodeURI(url), data, 'POST');
}

function* fetchEntityAnswer(action) {
  const { routeId, stopId, surveyQuestion } = action.payload;

  const results = yield call(fetchEntityAnswerApi, action.payload);

  if (!results.error) {
    yield put(actionCreators.fetchEntityAnswerSucceeded({ surveyQuestion, results, routeId, stopId }));
  } else {
    yield put(actionCreators.fetchEntityAnswerFailed({
      errorResource: 'routeSurveyModal.fetchAnswerError',
      surveyQuestion,
      routeId,
      stopId
    }));
  }
}

function* watchFetchEntityAnswer() {
  yield takeEvery(actions.fetchEntityAnswer, fetchEntityAnswer);
}
// END: FETCH ENTITY ANSWER

// START: FETCH SIGNATURE ANSWER
function* fetchSignatureAnswerApi(surveyAnswerId) {
  const url = `/Photo/Signature/${surveyAnswerId}/Image`;
  return yield call(defaultFetch, url);
}

function* fetchSignatureAnswer(action) {
  const { routeId, stopId, surveyAnswerId } = action.payload;
  const base64Image = yield call(fetchSignatureAnswerApi, surveyAnswerId);

  if (!base64Image.error) {
    yield put(actionCreators.fetchSignatureAnswerSucceeded({
      routeId,
      stopId,
      surveyAnswerId,
      base64Image
    }));
  } else {
    yield put(actionCreators.fetchSignatureAnswerFailed({
      errorResource: 'routeSurveyModal.fetchSignatureAnswerError',
      surveyAnswerId
    }));
  }
}

function* watchFetchSignatureAnswer() {
  yield takeEvery(actions.fetchSignatureAnswer, fetchSignatureAnswer);
}
// END: FETCH SIGNATURE ANSWER

function* routeSurveyModal() {
  yield all([
    watchFetchSurveys(),
    watchFetchPhotoAlbums(),
    watchFetchEntityAnswer(),
    watchFetchSignatureAnswer(),
    watchFailedFetchs()
  ]);
}

export default routeSurveyModal;
